import { Box, Button, Chip, IconButton, Snackbar, TextField, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../../theme';
import PublicIcon from '@mui/icons-material/Public';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Header from '../../../components/Header';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SortIcon from '@mui/icons-material/Sort';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { can, filterActionColumns } from '../../../utils/permissions';

const AllPackages = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const token = localStorage.getItem('token');

  const [allPackages, setallPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/packages?page=${page}&limit=${pageSize}&search=${encodeURIComponent(
          search
        )}`,
        { headers: { token } }
      );
      const data = await res.json();
      if (data.success) {
        setallPackages(data.packages);
        setTotalCount(data.pagination?.total || 0);
      } else {
        setOpenToast(true);
        setToastmessage(data.message || 'Error fetching packages');
      }
    } catch (error) {
      setOpenToast(true);
      setToastmessage('Error fetching packages');
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, search]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  // Delete package
  const handleDelete = async (id) => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/packages/${id}`, {
      method: 'DELETE',
      headers: { token },
    });
    const data = await res.json();
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
      if (data.success) {
        if (allPackages.length === 1 && page > 1) {
          setPage((p) => p - 1);
        } else {
          getData();
        }
      }
    }
  };

  const openDeleteDialog = (id) => {
    setIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setIdToDelete(null);
  };

  const confirmDelete = async () => {
    if (idToDelete == null) return;
    await handleDelete(idToDelete);
    closeDeleteDialog();
  };

  const columns = [
    {
      field: 'PackageImage',
      headerName: 'Package Image',
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <img
            src={cellValues.row.PackageImage}
            style={{ height: '100%', aspectRatio: '16/9', objectFit: 'cover', padding: '5px', maxWidth: '80px' }}
            alt="Package image"
          />
        );
      },
    },
    {
      field: 'PackageName',
      headerName: 'Package Name',
      flex: 1,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'PackageDescription',
      headerName: 'Package Description',
      flex: 1,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'CountryName',
      headerName: 'Country',
      flex: 1,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'PackagePrice',
      headerName: 'Price',
      flex: 0.5,
      cellClassName: 'name-column--cell',
      valueFormatter: (params) => `€ ${Number(params.value).toFixed(2)}`,
    },
    {
      field: 'sortOrder',
      headerName: 'Display order',
      width: 110,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'Edit',
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <Link to={`/package/${cellValues.row.PackageID}`}>
            <Button
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.greenAccent[700] },
              }}
              variant="contained"
            >
              <EditIcon />
            </Button>
          </Link>
        );
      },
    },
    {
      field: 'Duplicate',
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => {
        return (
          <Link to={`/package/${cellValues.row.PackageID}/duplicate`}>
            <Button
              sx={{
                backgroundColor: colors.grey[500],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.grey[600] },
              }}
              variant="contained"
            >
              <ContentCopyIcon />
            </Button>
          </Link>
        );
      },
    },
    {
      field: 'Delete',
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <Button
            sx={{
              backgroundColor: colors.redAccent[500],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.redAccent[600] },
            }}
            variant="contained"
            onClick={() => openDeleteDialog(cellValues.row.PackageID)}
          >
            <DeleteIcon />
          </Button>
        );
      },
    },
  ];

  // Toast
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="PACKAGES" subtitle="All Packages" />

        <Box display="flex" gap={1} flexWrap="wrap">
          {can('packages.edit') && (
            <Link to="/package/order">
              <Button
                sx={{
                  backgroundColor: colors.greenAccent[600],
                  color: colors.grey[100],
                  fontSize: '14px',
                  fontWeight: 'bold',
                  padding: '10px 20px',
                  '&:hover': { backgroundColor: colors.greenAccent[700] },
                }}
              >
                <SortIcon sx={{ mr: '10px' }} />
                Reorder packages
              </Button>
            </Link>
          )}
          {can('packages.create') && (
            <Link to="/package/addpackage">
              <Button
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: colors.grey[100],
                  fontSize: '14px',
                  fontWeight: 'bold',
                  padding: '10px 20px',
                  '&:hover': {
                    backgroundColor: colors.blueAccent[800],
                  },
                }}
              >
                <CardGiftcardIcon sx={{ mr: '10px' }} />
                Add Package
              </Button>
            </Link>
          )}
        </Box>
      </Box>

      <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center" m="10px 0 0 0">
        <TextField
          size="small"
          placeholder="Search by name, description, destination, price..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: colors.grey[300] }} />,
          }}
          sx={{ minWidth: '320px', backgroundColor: colors.primary[400] }}
        />
      </Box>

      {search && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 500 }}>
            Active filters:
          </Typography>
          <Chip
            label={`Search: ${search}`}
            size="small"
            onDelete={handleClearSearch}
            sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
          />
          {!loading && (
            <Typography variant="body2" sx={{ color: colors.greenAccent[400], ml: 1 }}>
              {totalCount} result{totalCount !== 1 ? 's' : ''}
            </Typography>
          )}
          <Typography
            variant="body2"
            onClick={handleClearSearch}
            sx={{
              color: colors.redAccent[500],
              cursor: 'pointer',
              ml: 1,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear all
          </Typography>
        </Box>
      )}

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
          },
          '& .name-column--cell': {
            color: colors.grey[100],
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: colors.primary[400],
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
          '& .MuiCheckbox-root': {
            color: `${colors.greenAccent[200]} !important`,
          },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
            color: `${colors.grey[100]} !important`,
            fontSize: 14,
          },
        }}
      >
        <DataGrid
          rows={allPackages}
          columns={filterActionColumns(columns, {
            Edit: 'packages.edit',
            Duplicate: 'packages.create',
            Delete: 'packages.delete',
          })}
          getRowId={(row) => row.PackageID}
          disableColumnFilter
          loading={loading}
          paginationMode="server"
          page={page - 1}
          pageSize={pageSize}
          rowCount={totalCount}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        title="Delete package?"
        message="Are you sure you want to delete this package?"
        colors={colors}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default AllPackages;
